import { useEffect, useRef, useState } from 'react';
import type { AnalysisResult } from '@/core/analysis/types';
import { chartChordPro, chartText, type SongInfo } from '@/core/export/chart';
import { chordsToMidi } from '@/core/export/midi';
import type { DisplayOptions } from '@/core/theory/display';
import { useHotkeys } from '@/lib/useHotkeys';
import { buttonClass } from '@/ui/button';
import { downloadFile, safeFileName } from './download';
import styles from './ExportMenu.module.css';

interface ExportMenuProps {
  song: SongInfo;
  result: AnalysisResult;
  options: DisplayOptions;
}

/** Print, copy and download the chord chart (F8.1–F8.4); everything is made on this device. */
export function ExportMenu({ song, result, options }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const base = safeFileName(song.title);

  useHotkeys({
    Escape: open
      ? () => {
          setOpen(false);
        }
      : undefined,
  });

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
    };
  }, [open]);

  const done = (message: string) => {
    setStatus(message);
    setOpen(false);
  };

  const items: { label: string; hint: string; run: () => void | Promise<void> }[] = [
    {
      label: 'Print or save as PDF',
      hint: 'Chart and chord diagrams',
      run: () => {
        setOpen(false);
        window.print();
      },
    },
    {
      label: 'Copy chords as text',
      hint: 'Paste into a message or note',
      run: async () => {
        try {
          await navigator.clipboard.writeText(chartText(song, result, options));
          done('Chords copied.');
        } catch {
          done('Copying was blocked; use “Download text” instead.');
        }
      },
    },
    {
      label: 'Download text',
      hint: '.txt',
      run: () => {
        downloadFile(`${base}.txt`, chartText(song, result, options), 'text/plain');
        done('Text file saved.');
      },
    },
    {
      label: 'Download ChordPro',
      hint: '.cho, for chord-sheet apps',
      run: () => {
        downloadFile(`${base}.cho`, chartChordPro(song, result, options), 'text/plain');
        done('ChordPro file saved.');
      },
    },
    {
      label: 'Download MIDI',
      hint: '.mid, chords and bass',
      run: () => {
        const midi = chordsToMidi(result, {
          transpose: options.transpose,
          bass: true,
          click: false,
        });
        downloadFile(`${base}.mid`, midi, 'audio/midi');
        done('MIDI file saved.');
      },
    },
    {
      label: 'Download MIDI with click',
      hint: '.mid, plus a metronome track',
      run: () => {
        const midi = chordsToMidi(result, {
          transpose: options.transpose,
          bass: true,
          click: true,
        });
        downloadFile(`${base} (click).mid`, midi, 'audio/midi');
        done('MIDI file saved.');
      },
    },
  ];

  return (
    <div ref={ref} className={styles.wrap}>
      <button
        type="button"
        className={buttonClass('secondary')}
        aria-expanded={open}
        onClick={() => {
          setStatus('');
          setOpen(!open);
        }}
      >
        Export
      </button>
      {open && (
        <div role="dialog" aria-label="Export chords" className={styles.menu}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              className={styles.item}
              onClick={() => void item.run()}
            >
              <span className={styles.label}>{item.label}</span>
              <span className={styles.hint}>{item.hint}</span>
            </button>
          ))}
        </div>
      )}
      <span role="status" className={styles.status}>
        {status}
      </span>
    </div>
  );
}
